import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Phone, MessageCircle, Loader2, Store, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/**
 * Public profile wall — key = NVN (12-digit) or user_id.
 * Shows: profile header, rating, wishlist button, recent posts.
 */
export default function ProfileWall() {
  const { key } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/feed/user/${key}`);
      setData(r.data);
      setFav(!!r.data.is_favorited);
    } catch { toast.error("Profile not found"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [key]);

  const toggleFav = async () => {
    if (!user) { toast.error("Sign in first"); return; }
    try {
      const r = await api.post("/favorites/toggle", { target_type: "user", target_id: data.profile.user_id });
      setFav(r.data.favorited);
      toast.success(r.data.favorited ? "Added to Wishlist" : "Removed");
    } catch {}
  };

  const submitRating = async () => {
    try {
      await api.post("/ratings", { target_type: "user", target_id: data.profile.user_id, stars, comment });
      toast.success(`Rated ${stars}★`);
      setRatingOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rating failed"); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!data) return <div className="p-12 text-center text-stone-500">Not found.</div>;
  const p = data.profile;

  return (
    <div className="max-w-2xl mx-auto px-3 py-4" data-testid="profile-wall">
      <div className="bg-gradient-to-br from-stone-900 to-[#2D5A27] text-white rounded-3xl p-5">
        <div className="flex items-start gap-4">
          {p.picture ? <img loading="lazy" src={p.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" /> :
            <div className="w-16 h-16 rounded-full bg-white/20 grid place-items-center font-black">{p.name?.[0] || "U"}</div>}
          <div className="flex-1 min-w-0">
            <div className="text-lg font-black">{p.name}</div>
            {p.nvn && <div className="font-mono text-[11px] text-emerald-200">NVN {p.nvn}</div>}
            <div className="mt-1 text-xs inline-flex items-center gap-1"><Star size={11} fill="currentColor" /> {data.rating.avg} ({data.rating.count})</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={toggleFav} className={`text-xs rounded-full px-3 py-1.5 font-bold ${fav ? "bg-rose-500 text-white" : "bg-white/15 text-white hover:bg-white/25"}`} data-testid="wall-fav-btn">
            <Heart size={12} className="inline mr-1" fill={fav ? "currentColor" : "none"} /> {fav ? "Wishlisted" : "Add to Wishlist"}
          </button>
          <button onClick={() => setRatingOpen(true)} className="text-xs rounded-full px-3 py-1.5 font-bold bg-white/15 hover:bg-white/25" data-testid="wall-rate-btn">
            <Star size={12} className="inline mr-1" /> Rate
          </button>
          {p.mobile && (
            <a href={`tel:${p.mobile}`} className="text-xs rounded-full px-3 py-1.5 font-bold bg-white/15 hover:bg-white/25" data-testid="wall-call">
              <Phone size={12} className="inline mr-1" /> Call
            </a>
          )}
          <Link to={`/store/${p.nvn || p.user_id}`} className="text-xs rounded-full px-3 py-1.5 font-bold bg-white/15 hover:bg-white/25" data-testid="wall-store">
            <Store size={12} className="inline mr-1" /> Store
          </Link>
        </div>
      </div>

      {ratingOpen && (
        <div className="mt-3 bg-white rounded-2xl border border-stone-200 p-4" data-testid="rating-form">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Your rating</div>
          <div className="flex gap-1 mb-2">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => setStars(n)} className={n <= stars ? "text-amber-500" : "text-stone-300"} data-testid={`rating-star-${n}`}>
                <Star size={22} fill="currentColor" />
              </button>
            ))}
          </div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment (optional)" rows={2} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E05E36]" data-testid="rating-comment" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setRatingOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitRating} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="rating-submit">Submit</Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {data.posts.length === 0 ? (
          <div className="text-center py-16 text-stone-500">No public posts yet.</div>
        ) : data.posts.map(p => (
          <div key={p.post_id} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`wall-post-${p.post_id}`}>
            <div className="text-[11px] text-stone-500">{relTime(p.created_at)}{p.city ? ` · ${p.city}` : ""}</div>
            {p.body && <div className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">{p.body}</div>}
            {p.images?.[0] && <img loading="lazy" src={p.images[0]} alt="" className="w-full rounded-xl mt-2 max-h-96 object-cover" />}
            <div className="mt-2 text-xs text-stone-500 flex gap-3">
              <span><Heart size={11} className="inline mr-1" /> {p.likes_count || 0}</span>
              <span><MessageCircle size={11} className="inline mr-1" /> {p.comments_count || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
